import { useEffect, useRef, useState } from "react";
import logo from "../logo2.png";
import { NavLink, useLocation } from "react-router-dom";
import { supabase } from "../admin/supabase";
import { getSiteSettings } from "../siteSettings";
import SiteSearch from "../Search/SiteSearch";
import "../Language/Language.css";


import {
  FaChevronDown,
  FaSearch,
  FaBars,
  FaTimes
} from "react-icons/fa";

const academicSchools = [
  { name: "School of Business and Economics", url: "https://sobe.zetech.ac.ke/" },
  { name: "School of ICT, Media and Engineering", url: "https://sime.zetech.ac.ke/" },
  { name: "School of Health Sciences", url: "https://healthsciences.zetech.ac.ke/" },
  { name: "Zetech University Law School", url: "https://lawschool.zetech.ac.ke/" },
  { name: "Directorate of Postgraduate Studies", url: "https://postgraduatestudies.zetech.ac.ke/" },
  { name: "School of Education, Arts and Social Sciences", url: "https://seass.zetech.ac.ke/" },
];

export default function Navbar() {
  const [siteSettings, setSiteSettings] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [schoolsOpen, setSchoolsOpen] = useState(false);
  const location = useLocation();

  const menuRef = useRef(null);
  const toggleRef = useRef(null);
  const lastFocusedRef = useRef(null);

  useEffect(() => {
    let isMounted = true;
    const loadSettings = async () => {
      const settings = await getSiteSettings(supabase);
      if (isMounted) setSiteSettings(settings);
    };

    loadSettings();
    return () => { isMounted = false; };
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenGroup(null);
    setSchoolsOpen(false);
  }, [location.pathname]);

  // Prevent body scroll while drawer open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Focus management when drawer opens/closes
  useEffect(() => {
    if (mobileOpen) {
      // Remember the element that opened the drawer
      lastFocusedRef.current = document.activeElement;

      // Move focus to the first focusable element inside the drawer
      const menu = menuRef.current;
      if (!menu) return;
      const focusable = menu.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length) {
        focusable[0].focus();
      } else {
        menu.focus();
      }
    } else if (lastFocusedRef.current && lastFocusedRef.current.focus) {
      // Return focus to the hamburger button (or whichever element opened it)
      lastFocusedRef.current.focus();
    }
  }, [mobileOpen]);

  // Focus trap + Escape key inside the drawer
  useEffect(() => {
    if (!mobileOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setMobileOpen(false);
        return;
      }
      if (e.key !== "Tab") return;

      const menu = menuRef.current;
      if (!menu) return;
      const focusable = Array.from(
        menu.querySelectorAll(
          'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mobileOpen]);

  // Global shortcut: Ctrl/Cmd + K opens site search
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setMobileOpen(false);
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const openSearch = () => {
    setMobileOpen(false);
    setSearchOpen(true);
  };


  // Closing the drawer when any link inside it is tapped — including a link to
  // the page the user is already on (route change effect never fires there).
  const handleMenuClick = (e) => {
    if (e.target.closest("a[href]")) {
      setMobileOpen(false);
      setOpenGroup(null);
      setSchoolsOpen(false);
    }
  };

  const toggleGroup = (name) => (e) => {
    e.preventDefault();
    setOpenGroup((cur) => (cur === name ? null : name));
  };

  const studyActive = [
    "/application-process",
    "/fee-payments",
    "/international-students",
    "/student-accommodation",
    "/faqs"
  ].includes(location.pathname);

  const academicsActive = [
    "/programmes",
    "/tuition-fees",
    "/exam-memos"
  ].includes(location.pathname);

  const researchActive = [
    "/research",
    "/institutes",
    "/conferences",
    "/innovations",
    "/research/projects",
    "/research/patents",
    "/research/consultancies",
    "/research/grants",
    "/ethics-review"
  ].includes(location.pathname);
  const studentLifeActive = [
    "/students-life",
    "/student-leadership",
    "/clubs&societies",
    "/student-support",
    "/scholarship&financial-aid",
    "/chaplaincy"
  ].includes(location.pathname);
  

  const aboutActive = [
    "/about-zu",
    "/mission-vision",
    "/quality-policy",
    "/governance",
    "/our-campuses",
    "/charter",
    "/our-foundation"
  ].includes(location.pathname);

  return (
    // <nav className="navbar" style={{ backgroundColor: siteSettings?.primary_color || undefined, color: siteSettings?.secondary_color || undefined }}>
      <nav className={`navbar ${mobileOpen ? "mobile-open" : ""}`} >

      <div className="container">

        <NavLink className="logo" to="/" aria-label="Zetech University home">
          <img loading="eager" decoding="async" src={siteSettings?.logo_url || logo} alt={siteSettings?.site_name || "Zetech University"} />
        </NavLink>

        <button
          type="button"
          ref={toggleRef}
          className="mobile-toggle"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="mobile-navigation-menu"
          onClick={() => setMobileOpen((v) => !v)}
        >
          {mobileOpen ? <FaTimes /> : <FaBars />}
        </button>

        <div
          className={`mobile-backdrop ${mobileOpen ? "show" : ""}`}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />

        <ul
          id="mobile-navigation-menu"
          ref={menuRef}
          className={mobileOpen ? "mobile-open" : ""}
          aria-hidden={!mobileOpen}
          role="dialog"
          aria-modal={mobileOpen}
          aria-label="Mobile navigation"
          tabIndex={-1}
          onClick={handleMenuClick}
        >

          {/* Drawer header (mobile only) */}
          <li className="drawer-head">
            <NavLink to="/" aria-label="Zetech University home" onClick={() => setMobileOpen(false)}>
              <img loading="eager" decoding="async"
                className="drawer-logo"
                src={siteSettings?.logo_url || logo}
                alt={siteSettings?.site_name || "Zetech University"}
              />
            </NavLink>
            <button
              type="button"
              className="drawer-close"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            >
              <FaTimes />
            </button>
          </li>




          {/* HOME */}

          <li>
            <NavLink
              to="/"
              end
              className={({ isActive }) => isActive ? "active-link" : ""}
            >
              HOME
            </NavLink>
          </li>

          {/* STUDY */}

          <li className={`dropdown ${studyActive ? "active" : ""} ${openGroup === "study" ? "open" : ""}`}>

            <button
              type="button"
              className="dropdown-toggle"
              onClick={toggleGroup("study")}
              aria-expanded={openGroup === "study"}
              aria-controls="study-dropdown-menu"
            >
              STUDY WITH US
              <FaChevronDown className="arrow" />
            </button>

            <ul id="study-dropdown-menu" className="dropdown-menu">

              <li>
                <NavLink
                  to="/application-process"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Application Process
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/fee-payments"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Fee Payments
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/international-students"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  International Students
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/student-accommodation"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Student Accommodation
                </NavLink>
              </li>

              <li>
                 <NavLink
                  to="/faqs"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  FAQs
                </NavLink>
              </li>

              <li>
                <a
                  href="https://sajili.zetech.ac.ke/index.php?mod=kuccps"
                  rel="noopener noreferrer"
                >
                  KUCCPS Students
                </a>
              </li>

            </ul>

          </li>

          {/* ACADEMICS */}

          <li className={`dropdown ${academicsActive ? "active" : ""} ${openGroup === "academics" ? "open" : ""}`}>

            <button
              type="button"
              className="dropdown-toggle"
              onClick={toggleGroup("academics")}
              aria-expanded={openGroup === "academics"}
              aria-controls="academics-dropdown-menu"
            >
              ACADEMICS
              <FaChevronDown className="arrow" />
            </button>

            <ul id="academics-dropdown-menu" className="dropdown-menu">

              <li>
                <NavLink
                  to="/programmes"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Programmes
                </NavLink>
              </li>

              <li className={`dropdown-submenu ${schoolsOpen ? "open" : ""}`}>
                <button
                  type="button"
                  className="submenu-toggle"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setSchoolsOpen((current) => !current);
                  }}
                  aria-expanded={schoolsOpen}
                  aria-controls="academic-schools-submenu"
                >
                  Schools
                  <FaChevronDown className="arrow" />
                </button>
                <ul id="academic-schools-submenu" className="submenu">
                  {academicSchools.map((school) => (
                    <li key={school.url}>
                      <a href={school.url} target="_blank" rel="noopener noreferrer">
                        {school.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </li>

              <li>
                <NavLink
                  to="/tuition-fees"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Tuition Fees
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/exam-memos"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Exam Memos
                </NavLink>
              </li>

              <li>
                <a
                  href="https://shahada.zetech.ac.ke"
                  rel="noopener noreferrer"
                >
                  Graduation
                </a>
              </li>

            </ul>

          </li>

          {/* RESEARCH */}
          <li className={`dropdown ${researchActive ? "active" : ""} ${openGroup === "research" ? "open" : ""}`}>

            <button
              type="button"
              className="dropdown-toggle"
              onClick={toggleGroup("research")}
              aria-expanded={openGroup === "research"}
              aria-controls="research-dropdown-menu"
            >
              RESEARCH
              <FaChevronDown className="arrow" />
            </button>

            <ul id="research-dropdown-menu" className="dropdown-menu">

              <li>
                <NavLink
                  to="/research"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Our Research
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/institutes"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Our Institutes
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/conferences"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Our Conferences
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/innovations"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Innovations
                </NavLink>
              </li>
              <li>
                <NavLink to="/research/grants" className={({ isActive }) => isActive ? "active-link" : ""}>
                  Grants &amp; Projects
                </NavLink>
              </li>
              <li>
                <NavLink to="/research/patents" className={({ isActive }) => isActive ? "active-link" : ""}>
                  Patents &amp; Intellectual Property
                </NavLink>
              </li>
              <li>
                <NavLink to="/research/consultancies" className={({ isActive }) => isActive ? "active-link" : ""}>
                  Consultancies
                </NavLink>
              </li>
               {/* <li>
                <NavLink
                  to="/ethics-review"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Ethics Review
                </NavLink>
              </li> */}
              <li>
                <a
                  href="https://iserc.zetech.ac.ke"
                  rel="noopener noreferrer"
                >
                  Ethics Review
                </a>
              </li>

            </ul>

          </li>

          {/* Student Life */}
          <li className={`dropdown ${studentLifeActive ? "active" : ""} ${openGroup === "student-life" ? "open" : ""}`}>

            <button
              type="button"
              className="dropdown-toggle"
              onClick={toggleGroup("student-life")}
              aria-expanded={openGroup === "student-life"}
              aria-controls="student-life-dropdown-menu"
            >
              STUDENT LIFE
              <FaChevronDown className="arrow" />
            </button>

            <ul id="student-life-dropdown-menu" className="dropdown-menu">

              <li>
                <NavLink
                  to="/students-life"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Life at Zetech
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/student-leadership"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Student Leadership
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/clubs&societies"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Clubs & Societies
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/chaplaincy"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Chaplaincy
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/student-support"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Student Support
                </NavLink>
              </li>
               <li>
                <NavLink
                  to="/scholarship&financial-aid"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Scholarships & Financial Aid
                </NavLink>
              </li>
             

            </ul>

          </li>

          {/* STUDENT LIFE */}

          {/* <li>
            <NavLink
              to="/students-life"
              className={({ isActive }) => isActive ? "active-link" : ""}
            >
              STUDENT LIFE
            </NavLink>
          </li> */}

          {/* ABOUT */}

          <li className={`dropdown ${aboutActive ? "active" : ""} ${openGroup === "about" ? "open" : ""}`}>

            <button
              type="button"
              className="dropdown-toggle"
              onClick={toggleGroup("about")}
              aria-expanded={openGroup === "about"}
              aria-controls="about-dropdown-menu"
            >
              ABOUT ZU
              <FaChevronDown className="arrow" />
            </button>

            <ul id="about-dropdown-menu" className="dropdown-menu">

              <li>
                <NavLink
                  to="/about-zu"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  About Us
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/mission-vision"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Mission, Vision, Philosophy & Core Values
                </NavLink>
              </li>

              {/* <li>
                <NavLink
                  to="/quality-policy"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Quality Policy
                </NavLink>
              </li> */}

              <li>
                <NavLink
                  to="/governance"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Governance
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/our-campuses"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Campuses
                </NavLink>
              </li>
               <li>
                <NavLink
                  to="/our-foundation"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Our Foundation
                </NavLink>
              </li>

              <li>
                <NavLink
                  to="/charter"
                  className={({ isActive }) => isActive ? "active-link" : ""}
                >
                  Charter & Accreditation
                </NavLink>
              </li>

            </ul>

          </li>

          {/* Search entry inside the mobile drawer */}
          <li className="drawer-search">
            <button type="button" className="drawer-search-btn" onClick={openSearch}>
              <FaSearch aria-hidden="true" />
              SEARCH
            </button>
          </li>

        </ul>

        <button
          type="button"
          className="search search-btn"
          onClick={openSearch}
          aria-label="Search the website"
          aria-haspopup="dialog"
          aria-expanded={searchOpen}
          title="Search (Ctrl + K)"
        >
          <FaSearch aria-hidden="true" />
        </button>

      </div>

      <SiteSearch open={searchOpen} onClose={() => setSearchOpen(false)} />

    </nav>
  );
}
